import {makeAutoObservable} from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import {api, t} from 'shared/core';
import {createStore, DomainStore} from 'shared/store';
import {errorAlert, pickedDay} from 'shared/tools';
import {auth, endpoints, types} from 'main/tools';
import {ScheduleRule, Shift, TimeOffPeriod, TimeTrackingDimension, TimeTrackingSchedule} from 'main/stores/time_tracking';

class ShiftSchedulerState {
  store = new DomainStore();
  scheduleId;
  schedules = [];
  schedule = null;
  dimensions = [];
  shifts = [];
  timeOff = [];
  weekStart = currentWeekStart();
  latestRequestId = 0;
  isUpdating = false;

  editingShift = new Shift();
  originalShift = null;
  errors = {};
  repeatScopeRequest = {};
  publishRequest = {};
  publishedShiftChangeRequest = {};
  copyingFromEmployee = null;
  copyErrors = {};
  missingAssignments = [];

  copyOnDrop = false;
  hoveredCell = null;

  constructor() {
    makeAutoObservable(this, {store: false});
  }

  receiveProps({scheduleId}) {
    this.scheduleId = scheduleId;
  }

  async load() {
    await this.store._compose([
      endpoints.TIME_TRACKING.SCHEDULES.ALL,
      endpoints.TIME_TRACKING.DIMENSIONS.WITH_ASSIGNED_EMPLOYEES
    ]);

    this.schedules = this.store._getAll(types.TIME_TRACKING.SCHEDULE, TimeTrackingSchedule);
    this.dimensions = this.store._getAll(types.TIME_TRACKING.DIMENSION, TimeTrackingDimension);
    this.schedule = _.find(this.schedules, schedule => schedule.id === this.scheduleId && schedule.configured) || null;

    await this.loadShifts();
  }

  async loadShifts() {
    if (!this.schedule) return;

    const weekParams = {
      start_date: this.weekStart.format('YYYY-MM-DD'),
      end_date: this.weekEnd.format('YYYY-MM-DD')
    };
    const store = new DomainStore();
    await this.latestRequest(
      () => store._compose([
        {
          url: endpoints.TIME_TRACKING.SHIFTS.ALL,
          params: {'filter[schedule_id]': this.schedule.id, ...weekParams}
        },
        {
          url: endpoints.TIME_TRACKING.SCHEDULES.TIME_OFF.with(this.schedule.id),
          params: weekParams
        }
      ]),
      () => {
        this.shifts = store._getAll(types.TIME_TRACKING.SHIFT).map(model => new Shift(model));
        this.timeOff = store._getAll(types.TIME_TRACKING.SCHEDULE_TIME_OFF_EVENT).map(model => new TimeOffPeriod(model));
      }
    );
  }

  get hasScheduleDirectory() {
    return auth.hasAccess('::MANAGE_TIME_TRACKING') || this.schedules.length > 1;
  }

  get sortedDimensions() {
    return _.sortBy(this.dimensions, 'order');
  }

  get days() {
    return _.range(7).map(offset => this.weekStart.clone().add(offset, 'days'));
  }

  get weekEnd() {
    return this.weekStart.clone().add(6, 'days');
  }

  get weekRange() {
    return t('components.dates.DATE_SPAN', {
      startDate: this.weekStart.format('MMM D'),
      endDate: this.weekEnd.format('MMM D, YYYY')
    });
  }

  isOverlapping(shift) {
    const period = shiftPeriod(shift);

    return _.some(this.assignedShifts, other =>
      other !== shift && other.employeeId === shift.employeeId && periodsOverlap(period, shiftPeriod(other))
    ) || _.some(this.timeOff, timeOff => timeOff.employeeId === shift.employeeId && periodsOverlap(period, timeOff));
  }

  get shiftsByEmployee() {
    return _.groupBy(this.shifts, 'employeeId');
  }

  get shiftsByEmployeeAndDay() {
    return _.mapValues(this.shiftsByEmployee, employeeShifts =>
      _.groupBy(_.sortBy(employeeShifts, 'startTimeFromString'), shift => moment(shift.date).format('YYYY-MM-DD'))
    );
  }

  get scheduledHoursByEmployee() {
    return _.mapValues(this.shiftsByEmployee, employeeShifts => _.sumBy(employeeShifts, 'paidHours'));
  }

  shiftsFor(employeeId, day) {
    return _.get(this.shiftsByEmployeeAndDay, [employeeId, day.format('YYYY-MM-DD')], []);
  }

  missingDimensionValues(shift, employeeId) {
    if (!employeeId) return [];

    const assignedDimensionValueIds = this.assignedDimensionValueIds(employeeId);

    return shift.dimensionValues.
      filter(value => !_.includes(assignedDimensionValueIds, value.id)).
      map(value => ({
        value,
        dimension: _.find(this.dimensions, {id: value.dimensionId}),
      }));
  }

  timeOffSegmentsFor(employeeId, day) {
    return _.sortBy(this.timeOff, timeOff => timeOff.startsAt.valueOf()).
      filter(timeOff => timeOff.employeeId === employeeId && timeOff.coversDay(day)).
      map(timeOff => ({
        timeOff,
        continuesBefore: !day.isSame(timeOff.startsAt, 'day') && !day.isSame(this.weekStart, 'day'),
        continuesAfter: !day.isSame(timeOff.lastDay, 'day') && !day.isSame(this.weekEnd, 'day')
      }));
  }

  scheduledHoursFor(employeeId) {
    return this.scheduledHoursByEmployee[employeeId] || 0;
  }

  get assignedShifts() {
    const scheduleEmployeeIds = new Set(this.schedule.employees.map(employee => employee.id));

    return this.shifts.filter(shift => scheduleEmployeeIds.has(shift.employeeId));
  }

  get assignedHours() {
    return _.sumBy(this.assignedShifts, 'paidHours');
  }

  assignedHoursOn(day) {
    return _.sumBy(this.assignedShifts.filter(shift => moment(shift.date).isSame(day, 'day')), 'paidHours');
  }

  applicableDimensionValues(dimension, employeeId) {
    const activeValues = dimension.values.filter(value => !value.isArchived);
    if (!employeeId) return activeValues;

    return activeValues.filter(value => _.some(value.employees, {id: employeeId}));
  }

  assignedDimensionValueIds(employeeId) {
    return _.flatMap(this.dimensions, dimension => dimension.values).
      filter(value => _.some(value.employees, {id: employeeId})).
      map(value => value.id);
  }

  goToPreviousWeek() {
    this.weekStart = this.weekStart.clone().subtract(1, 'week');
    this.loadShifts();
  }

  goToNextWeek() {
    this.weekStart = this.weekStart.clone().add(1, 'week');
    this.loadShifts();
  }

  async goToCurrentWeek() {
    this.weekStart = currentWeekStart();
    await this.loadShifts();
  }

  newShift(employee, day) {
    this.openEditShiftModal({schedule: this.schedule, employee, date: day.toDate()});
  }

  openEditShiftModal(shift) {
    this.originalShift = shift;
  }

  setRepeatEnabled(enabled) {
    this.editingShift.scheduleRule = enabled ?
      new ScheduleRule(this.originalShift.scheduleRule || {repeatDays: [moment(this.editingShift.date).day()]}) :
      null;
  }

  setRepeatEndsOn(date) {
    this.editingShift.scheduleRule.repeatEndsOn = date ? pickedDay(date).toDate() : null;
  }

  get scheduleRuleChanged() {
    return !_.isEqual(this.originalShift.scheduleRule?.toJS(), this.editingShift.scheduleRule?.toJS());
  }

  get shiftChanged() {
    return !_.isEqual(shiftAttributes(this.originalShift), shiftAttributes(this.editingShift));
  }

  get needsPropagateValue() {
    return !!this.originalShift.scheduleRule && !this.scheduleRuleChanged && this.shiftChanged;
  }

  async confirmPublishedShiftChange() {
    const {action, shift, employee, day} = this.publishedShiftChangeRequest;
    this.closePublishedShiftChangeModal();

    if (action === 'delete') {
      await this.deleteShift(shift);
    } else {
      await this.dropShift(shift.id, employee, day, true);
    }
  }

  async saveShift(propagate) {
    const shift = this.editingShift;
    const shiftStore = new DomainStore();
    const dryRun = propagate === undefined && this.needsPropagateValue;
    const propagateChanges = dryRun || (propagate ?? this.scheduleRuleChanged);

    const {errors} = await this.latestRequest(
      () => shift.isNew ?
        shiftStore.post(endpoints.TIME_TRACKING.SHIFTS.ALL, types.TIME_TRACKING.SHIFT, shift) :
        shiftStore.patch(`${endpoints.TIME_TRACKING.SHIFTS.with(shift.id)}?propagate=${propagateChanges}&dry_run=${dryRun}`, types.TIME_TRACKING.SHIFT, shift),
      ({errors}) => _.isEmpty(errors) && !dryRun && this.replaceShiftsFor([shift.employeeId], shiftStore._getAll(types.TIME_TRACKING.SHIFT, Shift))
    );

    this.errors = errors;
    if (!_.isEmpty(errors)) return;

    if (dryRun) {
      this.openRepeatScopeModal({action: 'update', shift, scope: 'this'});
    } else {
      this.closeEditShiftModal();
    }
  }

  requestDeleteShift(shift) {
    if (shift.scheduleRule) {
      this.openRepeatScopeModal({action: 'delete', shift, scope: 'this'});
    } else if (shift.published) {
      this.openPublishedShiftChangeModal({action: 'delete', shift});
    } else {
      this.deleteShift(shift);
    }
  }

  async deleteShift(shift, scope) {
    const shiftStore = new DomainStore();

    await this.latestRequest(
      () => shiftStore.destroy(types.TIME_TRACKING.SHIFT, shift, {propagate: scope === 'future'}),
      ({errors}) => _.isEmpty(errors) && this.replaceShiftsFor([shift.employeeId], shiftStore._getAll(types.TIME_TRACKING.SHIFT, Shift))
    );
  }

  setRepeatScope(scope) {
    this.repeatScopeRequest.scope = scope;
  }

  async confirmRepeatScope() {
    const {action, shift, scope} = this.repeatScopeRequest;
    this.closeRepeatScopeModal();

    if (action === 'update') {
      await this.saveShift(scope === 'future');
    } else {
      await this.deleteShift(shift, scope);
    }
  }

  replaceShiftsFor(employeeIds, shifts) {
    const replacedShiftIds = _.map(shifts, 'id');

    this.shifts = [
      ..._.reject(this.shifts, shift => _.includes(employeeIds, shift.employeeId) || _.includes(replacedShiftIds, shift.id)),
      ...shifts
    ];
  }

  async latestRequest(request, applyResponse) {
    const requestId = ++this.latestRequestId;
    this.isUpdating = true;

    try {
      const response = await request();
      if (this.latestRequestId === requestId) applyResponse(response);

      return response;
    } finally {
      if (this.latestRequestId === requestId) this.isUpdating = false;
    }
  }

  async dropShift(shiftId, employee, day, publishedChangeConfirmed = false) {
    const shift = _.find(this.shifts, {id: shiftId});
    const employeeId = employee ? employee.id : null;
    if (!this.copyOnDrop && shift.employeeId === employeeId && day.isSame(shift.date, 'day')) return;

    const missingValues = shift.employeeId === employeeId ? [] : this.missingDimensionValues(shift, employeeId);
    if (missingValues.length > 0) {
      this.openMissingDimensionsModal([{employee, missingValues}]);
      return;
    }

    if (!this.copyOnDrop && shift.published && !publishedChangeConfirmed) {
      this.openPublishedShiftChangeModal({action: 'drop', shift, employee, day});
      return;
    }

    const changes = {employee, date: day.toDate()};
    const shiftStore = new DomainStore();
    const request = this.copyOnDrop ?
      () => shiftStore.post(endpoints.TIME_TRACKING.SHIFTS.ALL, types.TIME_TRACKING.SHIFT, shiftWith(shift, {...changes, id: null, schedule: this.schedule, scheduleRule: null})) :
      () => shiftStore.patch(endpoints.TIME_TRACKING.SHIFTS.with(shift.id), types.TIME_TRACKING.SHIFT, shiftWith(shift, changes));

    await this.latestRequest(
      request,
      ({errors}) => _.isEmpty(errors) && this.replaceShiftsFor([employeeId], shiftStore._getAll(types.TIME_TRACKING.SHIFT, Shift))
    );
  }

  endShiftDrag(shiftId, dropped) {
    // There's a bug in Chromium browsers that make it so they sometimes don't fire the drop event when modifier keys are held.
    // This ensures that it triggers anyway, but only if the drop command did not fire or else we'd end up with two copies.
    if (!dropped && this.copyOnDrop && this.hoveredCell) {
      this.dropShift(shiftId, this.hoveredCell.employee, this.hoveredCell.day);
    }

    this.copyOnDrop = false;
    this.hoveredCell = null;
  }

  get publishableWeekShifts() {
    return this.assignedShifts.filter(shift => !shift.published);
  }

  unpublishedShiftsFor(employeeId) {
    return this.publishableWeekShifts.filter(shift => shift.employeeId === employeeId);
  }

  get canPublish() {
    return this.schedule.hasLink('publish');
  }

  employeeActionLinks(employee) {
    return _.compact([
      this.canPublish && this.unpublishedShiftsFor(employee.id).length > 0 && {
        text: 'time_tracking.shifts.Publish Shifts',
        action: () => this.requestPublish(employee)
      },
      this.shiftsByEmployee[employee.id] && {
        text: 'time_tracking.shifts.Copy Shifts to Employees',
        action: () => this.openCopyEmployeeShiftsModal(employee)
      },
      {
        text: 'time_tracking.shifts.Copy Previous Week',
        action: () => this.copyPreviousWeek(employee.id)
      }
    ]);
  }

  async requestWeekShifts(request, employeeIds) {
    try {
      await this.latestRequest(request, response => {
        const store = createStore();
        store.sync(response.data);
        this.replaceShiftsFor(employeeIds, store.getAll(types.TIME_TRACKING.SHIFT).map(shift => new Shift(shift)));
      });
      return {};
    } catch (error) {
      if (!error.formErrors) throw error;

      return {errors: error.formErrors};
    }
  }

  requestPublish(employee = null) {
    const shifts = employee ? this.unpublishedShiftsFor(employee.id) : this.publishableWeekShifts;

    this.openPublishConfirmationModal({employee, count: shifts.length});
  }

  async confirmPublish() {
    const {employee} = this.publishRequest;
    this.closePublishConfirmationModal();

    await this.publishWeek(employee?.id);
  }

  async publishWeek(employeeId = null) {
    const payload = {start_date: this.weekStart.format('YYYY-MM-DD')};
    if (employeeId) payload.employee_id = employeeId;

    const {errors} = await this.requestWeekShifts(() => api.patch(this.schedule.link('publish'), payload), [employeeId]);
    if (errors) errorAlert(_.values(errors).join(' '));
  }

  async copyPreviousWeek(employeeId = null) {
    const payload = {schedule_id: this.schedule.id, start_date: this.weekStart.format('YYYY-MM-DD')};
    if (employeeId) payload.employee_id = employeeId;

    const {errors} = await this.requestWeekShifts(() => api.post(endpoints.TIME_TRACKING.SHIFTS.COPY_PREVIOUS_WEEK, payload), [employeeId]);
    if (errors) errorAlert(_.values(errors).join(' '));
  }

  get copyableEmployees() {
    return this.schedule.employees.filter(employee => employee.id !== this.copyingFromEmployee?.id);
  }

  get copyingFromEmployeeShifts() {
    return this.shiftsByEmployee[this.copyingFromEmployee?.id] || [];
  }

  async copyEmployeeShifts(targetEmployees) {
    const missingAssignments = targetEmployees.
      map(employee => ({
        employee,
        missingValues: _.uniqBy(
          _.flatMap(this.copyingFromEmployeeShifts, shift => this.missingDimensionValues(shift, employee.id)),
          'value.id'
        )
      })).
      filter(assignment => assignment.missingValues.length > 0);

    if (missingAssignments.length > 0) {
      this.closeCopyEmployeeShiftsModal();
      this.openMissingDimensionsModal(missingAssignments);
      return;
    }

    const targetEmployeeIds = _.map(targetEmployees, 'id');
    const {errors} = await this.requestWeekShifts(
      () => api.post(endpoints.TIME_TRACKING.SHIFTS.COPY, {
        schedule_id: this.schedule.id,
        employee_id: this.copyingFromEmployee.id,
        employee_ids: targetEmployeeIds,
        start_date: this.weekStart.format('YYYY-MM-DD')
      }),
      targetEmployeeIds
    );

    this.copyErrors = errors || {};
    if (!errors) this.closeCopyEmployeeShiftsModal();
  }
}

function currentWeekStart() {
  return moment().day(0).startOf('day');
}

function shiftAttributes(shift) {
  const {startTime, endTime, unpaidBreakMinutes, note} = shift.toJS();

  return {
    startTime,
    endTime,
    unpaidBreakMinutes: Number(unpaidBreakMinutes),
    note,
    employeeId: shift.employeeId,
    dimensionValueIds: _.sortBy(shift.dimensionValueIds)
  };
}

function shiftWith(shift, changes) {
  return Object.assign(new Shift(shift), changes);
}

function shiftPeriod(shift) {
  return {startsAt: shift.startTimeFromString, endsAt: shift.endTimeFromString};
}

function periodsOverlap(period, other) {
  return period.startsAt.isBefore(other.endsAt) && period.endsAt.isAfter(other.startsAt);
}

export default ShiftSchedulerState;
